/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { useNavigate } from 'react-router-dom'
import { grey } from '@mui/material/colors'

import { View, Text, Button, Pressable } from '@components/atoms'
import { CookieConsent } from '@utils'

/**
 * 분석 쿠키 동의 배너.
 * 아직 선택하지 않은 방문자에게만 노출되고, 선택 전까지 GA 는 초기화되지 않는다.
 */
export const CookieConsentBanner = () => {
	// hook
	const navigate = useNavigate()

	// state
	const [isVisible, setIsVisible] = React.useState(() => CookieConsent.get() === null)

	React.useEffect(() => {
		const unsubscribe = CookieConsent.subscribe(value => {
			setIsVisible(value === null)
		})

		return unsubscribe
	}, [])

	if (!isVisible) return null

	return (
		<View css={style} role="dialog" aria-label="Cookie consent">
			<View className="messageView">
				<Text className="messageText">
					Essential cookies keep this site running. If you allow analytics cookies, we can
					see which pages people actually use and make them better. Nothing here is used
					to identify you personally, and you can change your choice at any time.
				</Text>
				<Pressable
					className="policyLink"
					onClick={() => navigate('/privacy-policy')}
					aria-label="Read the Privacy Policy"
				>
					<Text className="policyLinkText">Privacy Policy</Text>
				</Pressable>
			</View>

			<View className="buttonView">
				<Button
					className="declineButton"
					variant="outlined"
					onClick={() => CookieConsent.set('denied')}
				>
					Essential only
				</Button>
				<Button
					className="acceptButton"
					variant="contained"
					onClick={() => CookieConsent.set('granted')}
				>
					Accept analytics
				</Button>
			</View>
		</View>
	)
}

const style = css({
	position: 'fixed',
	bottom: 0,
	left: 0,
	right: 0,
	zIndex: 1300,

	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	gap: '2.5rem',

	minHeight: '7.5rem',
	padding: '2rem 2.5rem',
	background: grey[900],
	color: 'white',
	boxShadow: '0 -2px 12px rgba(0, 0, 0, 0.25)',

	'.messageView': {
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: '0.75rem',
		maxWidth: '60rem',

		'.messageText': {
			margin: 0,
			textAlign: 'left',
			fontSize: '1.0625rem',
			lineHeight: 1.65,
		},

		'.policyLink': {
			width: 'fit-content',
			height: 'fit-content',

			'.policyLinkText': {
				margin: 0,
				fontSize: '1.0625rem',
				textDecoration: 'underline',
			},
		},
	},

	'.buttonView': {
		display: 'flex',
		flexShrink: 0,
		gap: '1rem',

		'.MuiButton-root': {
			padding: '0.75rem 1.5rem',
			fontSize: '1rem',
			whiteSpace: 'nowrap',
		},

		'.declineButton': {
			color: 'white',
			borderColor: grey[500],
		},
	},

	'@media (max-width: 720px)': {
		display: 'block',
		minHeight: 'auto',
		padding: '1.5rem 1.25rem',

		'.messageView': {
			'.messageText': {
				fontSize: '1rem',
			},
			'.policyLink .policyLinkText': {
				fontSize: '1rem',
			},
		},

		'.buttonView': {
			justifyContent: 'flex-end',
			marginTop: '1.5rem',

			'.MuiButton-root': {
				padding: '0.625rem 1.125rem',
				fontSize: '0.9375rem',
			},
		},
	},

	'@media (max-width: 420px)': {
		'.buttonView': {
			flexDirection: 'column-reverse',

			'.MuiButton-root': {
				width: '100%',
			},
		},
	},
})
